import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import Popup from "../index";
import Button from "../../common/forms/button/index";
import Actions from "../../../actions/index";
import {
  PopupsConsumer,
  PopupsContext,
} from "../../../helpers/contexts/popupsContext";
import "./styles/index.scss";
import { clickToLinkHandler } from "../../../helpers/utils";
import CloseIcon from "@mui/icons-material/Close";
import passwordValidator from "password-validator";
import DKFormTextInput from "../../common/widgets/formVariablesWidget/DKFormTextInput";

const schema = new passwordValidator();

schema
  .is()
  .min(8, "Password must be at least 8 characters long.")
  .has()
  .uppercase(1, "Password must contain at least 1 uppercase letter.")
  .has()
  .lowercase(1, "Password must contain at least 1 lowercase letter.")
  .has()
  .digits(1, "Password must contain at least 1 digit.")
  .has()
  .symbols(
    1,
    "Password must contain at least 1 special character such as $ % &."
  )
  .has()
  .not()
  .spaces(0, "Password cannot contain spaces.");

interface ChangePasswordPopupProps {
  user: { email: string; role_id: number; last_activity: Date };
  redirect: string;
  isOpen: boolean;
  canDismiss: boolean;
  changePassword: (data) => void;
}

function ChangePasswordPopup({
  user,
  redirect,
  isOpen = false,
  canDismiss = false,
  changePassword,
}: ChangePasswordPopupProps) {
  const [data, setData] = useState({
    password: undefined,
    confirm_password: undefined,
  });
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<boolean | any[]>([]); //TODO: improve typing
  const [isPasswordEdited, setIsPasswordEdited] = useState(false);
  const [isConfirmPasswordEdited, setIsConfirmPasswordEdited] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(isOpen);
  }, [isOpen, user]);

  const popupContext = useContext(PopupsContext);

  useEffect(() => {
    if (!user.last_activity) {
      popupContext.openChangePasswordPopup();
    }
  }, []);

  useEffect(() => {
    //check password complexity and validation
    if (data.password) {
      const updatedPasswordErrors = schema.validate(data.password, {
        details: true,
      });

      setPasswordErrors(updatedPasswordErrors);
    }

    //check if passwords match
    if (
      data.password &&
      data.confirm_password &&
      data.password === data.confirm_password
    ) {
      setPasswordsMatch(true);
    } else {
      setPasswordsMatch(false);
    }
  }, [data]);

  const handleChangePassword = (e) => {
    if (e.type === "click" || (e.type === "keydown" && e.keyCode === 13)) {
      changePassword(data);

      popupContext.closeChangePasswordPopup();

      if (redirect) {
        clickToLinkHandler(redirect);
      }
    }
  };

  const handleModalDismiss = () => {
    setIsVisible(!isVisible);
  };

  const handleFieldChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const canUpdatePassword = () => {
    return (
      data.password &&
      data.confirm_password &&
      passwordsMatch &&
      Array.isArray(passwordErrors) &&
      passwordErrors.length === 0
    );
  };

  const customPasswordMatchError = () => {
    if (isConfirmPasswordEdited && data.confirm_password?.length > 0) {
      if (passwordsMatch) {
        return undefined;
      } else {
        return "Passwords must match";
      }
    } else {
      return undefined;
    }
  };

  return (
    <PopupsConsumer>
      {({ closeChangePasswordPopup }) => (
        <Popup isOpen={isVisible} onClosePopup={closeChangePasswordPopup}>
          <div className="popup-container">
            <div className="form-container">
              <div className="change-password-body">
                <div className="popup-change-password-close-icon">
                  {canDismiss && <CloseIcon onClick={handleModalDismiss} />}
                </div>
                <div className="change-password-container">
                  <div className="popup-change-password-title">
                    Update Your Password
                  </div>
                  <div className="popup-change-password-container-field">
                    <DKFormTextInput
                      name={"password"}
                      label={"New Password"}
                      dataTestId="new-password-input"
                      value={data.password}
                      required
                      customErrorLabel={
                        isPasswordEdited && passwordErrors[0]
                          ? passwordErrors[0]?.message
                          : undefined
                      }
                      type="password"
                      onBlur={() => {
                        setIsPasswordEdited(true);
                      }}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <div className="popup-change-password-container-field">
                    <DKFormTextInput
                      name={"confirm_password"}
                      label={"Confirm New Password"}
                      dataTestId="confirm-new-password-input"
                      value={data.confirm_password}
                      required
                      customErrorLabel={customPasswordMatchError()}
                      type="password"
                      onBlur={() => {
                        setIsConfirmPasswordEdited(true);
                      }}
                      onChange={handleFieldChange}
                    />
                  </div>

                  <Button
                    type={canUpdatePassword() ? "primary" : "disabled-primary"}
                    size="large"
                    className="popup-change-password-button"
                    onClick={
                      canUpdatePassword() ? handleChangePassword : undefined
                    }
                  >
                    Update Password
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Popup>
      )}
    </PopupsConsumer>
  );
}

const mapStateToProps = (state) => {
  const { formErrors, user } = state.auth;
  let errors;
  if (formErrors) {
    errors = {
      data: formErrors,
    };
  }

  return {
    errors,
    user,
  };
};

const mapDispatchToProps = (dispatch) => ({
  changePassword: function (data) {
    dispatch(Actions.changePassword(data));
  },
});

//ChangePasswordPopup.contextType = PopupsContext;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangePasswordPopup);
